/**
 * Remembers which notice banners the customer has dismissed.
 *
 * Stored per device in localStorage rather than against the account: banners are shown to anonymous
 * visitors too, and a dismissal is a convenience ("I've read this"), not something worth a round trip.
 * Entries are keyed by banner id, so editing a banner's text does not resurrect it — staff who want a
 * changed notice seen again should create a new one.
 */

const STORAGE_KEY = 'dismissed-notice-banners';

export function getDismissedBannerIds(): string[] {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return [];
		}

		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
	} catch {
		// Private browsing, a disabled store or a corrupt value — showing the banner again is the safe
		// failure here.
		return [];
	}
}

export function addDismissedBannerId(id: string): string[] {
	const next = Array.from(new Set([...getDismissedBannerIds(), id]));

	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
	} catch {
		// Nothing to do: the dismissal still applies for this page view via component state.
	}

	return next;
}

/**
 * Drops ids that no longer correspond to a live banner, so the list can't grow without bound as
 * banners are deleted over the years.
 */
export function pruneDismissedBannerIds(liveIds: string[]): void {
	const live = new Set(liveIds);
	const kept = getDismissedBannerIds().filter(id => live.has(id));

	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
	} catch {
		// Ignored — pruning is housekeeping, not behaviour.
	}
}
